import {
  Activity,
  FileText,
  Home,
  LockKeyhole,
  Menu,
  QrCode,
  Settings,
  ShieldCheck,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useConnectivity } from "../hooks/useConnectivity";
import { navigate } from "../app/router";

interface AppShellProps {
  activePath: string;
  children: ReactNode;
  title: string;
  eyebrow?: string;
  actions?: ReactNode;
}

const navigation = [
  { path: "/", label: "Home", icon: Home },
  { path: "/quotations", label: "Quotations", icon: FileText },
  { path: "/transfer", label: "File Transfer", icon: QrCode },
  { path: "/activity", label: "Activity", icon: Activity },
] as const;

const utilities = [
  { path: "/settings", label: "Settings", icon: Settings },
  { path: "/privacy", label: "Privacy", icon: LockKeyhole },
  { path: "/compatibility", label: "Compatibility", icon: ShieldCheck },
] as const;

function isNavigationActive(itemPath: string, activePath: string): boolean {
  if (itemPath === "/") {
    return activePath === "/";
  }
  if (itemPath === "/transfer") {
    return ["/transfer", "/receive", "/link", "/download"].some(
      (path) => activePath === path || activePath.startsWith(`${path}/`),
    );
  }
  return activePath === itemPath || activePath.startsWith(`${itemPath}/`);
}

function NavItem({
  activePath,
  label,
  path,
  icon: Icon,
  onNavigate,
}: {
  activePath: string;
  label: string;
  path: string;
  icon: typeof Home;
  onNavigate?: () => void;
}) {
  const active = isNavigationActive(path, activePath);

  return (
    <button
      className={`nav-item${active ? " is-active" : ""}`}
      type="button"
      aria-current={active ? "page" : undefined}
      onClick={() => {
        navigate(path);
        onNavigate?.();
      }}
    >
      <Icon aria-hidden="true" size={20} strokeWidth={1.8} />
      <span>{label}</span>
    </button>
  );
}

export function AppShell({
  activePath,
  actions,
  children,
  eyebrow,
  title,
}: AppShellProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const connectivity = useConnectivity();
  const status =
    connectivity === "online"
      ? { label: "Online", icon: Wifi, tone: "online" }
      : connectivity === "offline-ready"
        ? { label: "Offline ready", icon: WifiOff, tone: "ready" }
        : { label: "Offline - first load incomplete", icon: WifiOff, tone: "warn" };
  const StatusIcon = status.icon;

  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    sidebarRef.current?.querySelector<HTMLButtonElement>(".nav-item")?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        window.requestAnimationFrame(() => menuButtonRef.current?.focus());
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [menuOpen]);

  return (
    <div className="app-shell">
      <a
        className="skip-link"
        href="#main-content"
        onClick={(event) => {
          event.preventDefault();
          document.querySelector<HTMLElement>("#main-content")?.focus();
        }}
      >
        Skip to content
      </a>
      <aside className={`sidebar${menuOpen ? " is-open" : ""}`} ref={sidebarRef}>
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            QT
          </span>
          <span>
            <strong>Quotation</strong>
            <small>&amp; Transfer</small>
          </span>
        </div>

        <nav className="sidebar-nav" aria-label="Primary">
          {navigation.map((item) => (
            <NavItem
              {...item}
              activePath={activePath}
              key={item.path}
              onNavigate={() => setMenuOpen(false)}
            />
          ))}
        </nav>

        <nav className="sidebar-nav utility-nav" aria-label="Utilities">
          {utilities.map((item) => (
            <NavItem
              {...item}
              activePath={activePath}
              key={item.path}
              onNavigate={() => setMenuOpen(false)}
            />
          ))}
        </nav>
      </aside>

      {menuOpen ? (
        <button
          className="sidebar-backdrop"
          type="button"
          aria-label="Close navigation"
          onClick={() => {
            setMenuOpen(false);
            window.requestAnimationFrame(() => menuButtonRef.current?.focus());
          }}
        />
      ) : null}

      <div className="app-main">
        <header className="topbar">
          <button
            className="icon-button mobile-menu"
            type="button"
            ref={menuButtonRef}
            aria-label={menuOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={menuOpen}
            onClick={() => {
              setMenuOpen((current) => {
                if (current) {
                  window.requestAnimationFrame(() => menuButtonRef.current?.focus());
                }
                return !current;
              });
            }}
          >
            {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
          <div className={`connectivity connectivity-${status.tone}`}>
            <StatusIcon aria-hidden="true" size={16} />
            <span>{status.label}</span>
          </div>
          <button
            className="icon-button top-settings"
            type="button"
            aria-label="Open settings"
            onClick={() => navigate("/settings")}
          >
            <Settings aria-hidden="true" size={20} />
          </button>
        </header>

        <main className="page" id="main-content" tabIndex={-1}>
          <header className="page-header">
            <div>
              {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
              <h1 id="page-title" tabIndex={-1}>
                {title}
              </h1>
            </div>
            {actions ? <div className="page-actions">{actions}</div> : null}
          </header>
          {children}
        </main>
      </div>

      <nav className="mobile-bottom-nav" aria-label="Primary">
        {navigation.map((item) => {
          const Icon = item.icon;
          const active = isNavigationActive(item.path, activePath);
          return (
            <button
              className={active ? "is-active" : ""}
              type="button"
              key={item.path}
              aria-current={active ? "page" : undefined}
              onClick={() => navigate(item.path)}
            >
              <Icon aria-hidden="true" size={20} />
              <span>{item.label === "File Transfer" ? "Transfer" : item.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
